import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { COLUMN_DEFINITIONS, REG_FIELD_ID_PREFIX } from '../../components/listsv2/profile-column-definitions';
import { parseColsParam, toggleColumn } from '../../utils';
import { useCallbackRef } from '@/utils/use-callback-ref';

const DEFAULT_COLUMN_ORDER = COLUMN_DEFINITIONS.map((c) => c.id);
const KNOWN_STATIC_IDS = new Set(DEFAULT_COLUMN_ORDER);

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

interface UseColumnStateParams {
  storageKey: string | null;
  searchParams: URLSearchParams;
  setSearchParams: SetSearchParams;
  defaultColumnIds: string[];
  onFieldsChange: (enabledColumns: string[]) => void;
}

export function useColumnState({
  storageKey,
  searchParams,
  setSearchParams,
  defaultColumnIds,
  onFieldsChange,
}: UseColumnStateParams) {
  const [enabledColumns, setEnabledColumns] = useState<string[]>(() => {
    const fromUrl = parseColsParam(
      searchParams.get('cols'),
      KNOWN_STATIC_IDS,
      DEFAULT_COLUMN_ORDER,
      REG_FIELD_ID_PREFIX
    );
    if (fromUrl) return fromUrl.enabledColumns;
    const fromStorage = storageKey
      ? parseColsParam(localStorage.getItem(storageKey), KNOWN_STATIC_IDS, DEFAULT_COLUMN_ORDER, REG_FIELD_ID_PREFIX)
      : null;
    return fromStorage?.enabledColumns ?? defaultColumnIds;
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const fromUrl = parseColsParam(
      searchParams.get('cols'),
      KNOWN_STATIC_IDS,
      DEFAULT_COLUMN_ORDER,
      REG_FIELD_ID_PREFIX
    );
    if (fromUrl) return fromUrl.columnOrder;
    const fromStorage = storageKey
      ? parseColsParam(localStorage.getItem(storageKey), KNOWN_STATIC_IDS, DEFAULT_COLUMN_ORDER, REG_FIELD_ID_PREFIX)
      : null;
    return fromStorage?.columnOrder ?? DEFAULT_COLUMN_ORDER;
  });

  const onFieldsChangeRef = useCallbackRef(onFieldsChange);

  // Notify the caller whenever enabledColumns changes so they can sync Redux fields.
  useEffect(() => {
    onFieldsChangeRef.current(enabledColumns);
  }, [enabledColumns, onFieldsChangeRef]);

  // Keep URL params and localStorage in sync with the current column selection.
  useEffect(() => {
    const orderedEnabled = columnOrder.filter((id) => enabledColumns.includes(id));
    const defaultOrdered = DEFAULT_COLUMN_ORDER.filter((id) => defaultColumnIds.includes(id));
    const isDefault =
      orderedEnabled.length === defaultOrdered.length && orderedEnabled.every((id, i) => id === defaultOrdered[i]);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (isDefault) {
          next.delete('cols');
        } else {
          next.set('cols', orderedEnabled.join(','));
        }
        return next;
      },
      { replace: true }
    );

    if (storageKey) {
      if (isDefault) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, orderedEnabled.join(','));
      }
    }
  }, [enabledColumns, columnOrder, setSearchParams, storageKey, defaultColumnIds]);

  const handleColumnToggle = useCallback(
    (columnId: string, enabled: boolean) => {
      const { enabledColumns: next, columnOrder: nextOrder } = toggleColumn(
        enabledColumns,
        columnOrder,
        columnId,
        enabled
      );
      setEnabledColumns(next);
      setColumnOrder(nextOrder);
    },
    [columnOrder, enabledColumns]
  );

  const handleColumnReorder = useCallback((newOrder: string[]) => {
    setColumnOrder(newOrder);
  }, []);

  return { enabledColumns, columnOrder, handleColumnToggle, handleColumnReorder };
}
